/**
 * @description 埋点事件构建器：mega menu 前缀、加购/点击 payload
 */

import { buildCategoryTrackFields, buildProductTrackFields, buildSchemeTrackFields, resolveCategoryTrackingPrefix } from './helpers'
import { buildHoverPageTracking } from './pageRegistry'
import {
  rememberProductEntrySource,
  resolveTrackSourceFromContext,
} from './sourceContext'

/**
 * @description 解析 Header mega menu 埋点前缀
 * @param {string} menuKey
 * @param {Array} topCategoryNavItems
 * @returns {string}
 */
export function resolveMegaMenuPrefix(menuKey, topCategoryNavItems = []) {
  if (menuKey === 'store') return 'MallHomepage'
  const index = topCategoryNavItems.findIndex((item) => item.key === menuKey)
  const item = index >= 0 ? topCategoryNavItems[index] : null
  return resolveCategoryTrackingPrefix(
    { categoryName: item?.label, categoryId: item?.categoryId },
    Math.max(index, 0)
  )
}

/**
 * @description 构建 mega menu 悬停页 PV 配置
 * @param {string} menuKey
 * @param {Array} topCategoryNavItems
 * @returns {Object}
 */
export function buildMegaMenuHoverTrack(menuKey, topCategoryNavItems = []) {
  if (menuKey === 'store') {
    return {
      pageKey: 'pv_MallHomepage_Hover_page',
      pvEvent: 'pv_MallHomepage_Hover',
      pageName: '商城首页悬浮页',
      targetType: 'mall_homepage_hover',
      targetId: 'mall_homepage_hover',
      targetName: '商城首页',
    }
  }
  const item = topCategoryNavItems.find((navItem) => navItem.key === menuKey)
  const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems)
  return buildHoverPageTracking(prefix, item?.categoryId, item?.label)
}

/**
 * @description 商城商品卡片标准 PV 事件名（对齐需求文档 ProductCard / ActivityCard）
 */
export const STORE_PRODUCT_EVENTS = {
  mallHomepageProd: 'pv_MallHomepage_ProductCard',
  mallHomepageProdClick: 'pv_MallHomepage_ProductCard_click',
  /** 列表页按商品细分曝光/点击（需求在页 PV 备注中要求按型号细分） */
  mallListProd: 'pv_MallList_ProductCard',
  mallListProdClick: 'pv_MallList_ProductCard_click',
  /** 活动聚合页商品曝光/点击（需求复用 MallHomepage_ActivityCard） */
  activityPageProd: 'pv_MallHomepage_ActivityCard',
  activityPageProdClick: 'pv_MallHomepage_ActivityCard_click',
}

/**
 * @description 方案卡片标准 PV 事件名（对齐需求 HotScheme；列表页为扩展事件）
 */
export const STORE_SCHEME_EVENTS = {
  schemeCard: 'pv_Scheme_HotScheme',
  schemeCardClick: 'pv_Scheme_HotScheme_click',
  schemeListScheme: 'pv_SchemeList_scheme',
  schemeListClick: 'pv_SchemeList_click',
}

export function buildSchemeClickPayload({ pvEvent, pageName, scheme, targetType = 'scheme_card', extra = {} }) {
  const fields = buildSchemeTrackFields(scheme)
  return {
    pvEvent,
    targetType,
    targetId: fields.targetId,
    targetName: fields.targetName,
    pageName,
    item: scheme,
    properties: { ...fields.properties, ...extra },
  }
}

/**
 * @description 构建商品曝光 safeTrack payload
 * @param {Object} options
 * @returns {Object}
 */
export function buildProductImpressionPayload({
  eventName,
  pageName,
  product,
  targetType,
  sourceType,
  sourceId,
  sourceName,
  fromPage = '',
  extra = {},
}) {
  const fields = buildProductTrackFields(product)
  const source = resolveTrackSourceFromContext(
    { sourceType, sourceId, sourceName, fromPage, pageName },
    product
  )
  return {
    eventName,
    eventType: 'impression',
    targetType,
    targetId: fields.targetId,
    targetName: fields.targetName,
    pageName,
    product,
    item: product,
    sourceType: source.sourceType,
    sourceId: source.sourceId,
    sourceName: source.sourceName,
    properties: { ...fields.properties, ...source.properties, ...extra },
  }
}

/**
 * @description 构建加购埋点 track 配置（传给 cartStore.addToCart）
 * @param {Object} options
 * @returns {Object}
 */
export function buildAddToCartTrack({
  eventName,
  pageName,
  fromPage = '',
  product,
  sourceType,
  sourceId,
  sourceName,
  trackContext,
}) {
  const fields = buildProductTrackFields(product)
  const source = resolveTrackSourceFromContext(
    trackContext || { sourceType, sourceId, sourceName, fromPage, pageName },
    product
  )
  return {
    eventName,
    pageName,
    fromPage: source.fromPage || fromPage,
    sourceType: source.sourceType,
    sourceId: source.sourceId,
    sourceName: source.sourceName,
    targetId: fields.targetId,
    targetName: fields.targetName,
    modelNo: fields.modelNo,
    item: product,
    product,
    properties: { ...fields.properties, ...source.properties },
  }
}

/**
 * @description 构建商品点击 trackClickEvent 参数
 * @param {Object} options
 * @returns {Object}
 */
export function buildProductClickPayload({
  pvEvent,
  pageName,
  product,
  targetType = 'mall_product',
  sourceType,
  sourceId,
  sourceName,
  fromPage = '',
  trackContext,
  rememberEntry = true,
  extra = {},
}) {
  const fields = buildProductTrackFields(product)
  const source = resolveTrackSourceFromContext(
    trackContext || { sourceType, sourceId, sourceName, fromPage, pageName },
    product
  )
  if (rememberEntry) {
    rememberProductEntrySource(fields.targetId, source)
  }
  return {
    pvEvent,
    targetType,
    targetId: fields.targetId,
    targetName: fields.targetName,
    pageName,
    product,
    item: product,
    sourceType: source.sourceType,
    sourceId: source.sourceId,
    sourceName: source.sourceName,
    properties: { ...fields.properties, ...source.properties, ...extra },
  }
}

/**
 * @description 构建分类点击 trackClickEvent 参数
 * @param {Object} options
 * @returns {Object}
 */
export function buildCategoryClickPayload({ pvEvent, pageName, category, targetType = 'mall_category', extra = {} }) {
  const fields = buildCategoryTrackFields(category)
  return {
    pvEvent,
    targetType,
    targetId: fields.targetId,
    targetName: fields.targetName,
    pageName,
    properties: { ...fields.properties, ...extra },
  }
}

/**
 * @description 分类首页商品卡片/加购等动态事件名（对齐需求 _Card）
 * @param {string} prefix
 * @returns {Object}
 */
export function buildCategoryPkTrackEvents(prefix) {
  return {
    productCardPv: `pv_${prefix}_Card`,
    productCardClick: `pv_${prefix}_Card_click`,
    addToCart: `pv_${prefix}_AddToCart_click`,
    buyNow: `pv_${prefix}_BuyNow_click`,
    addComparison: `pv_${prefix}_AddComparison_click`,
    removeComparison: `pv_${prefix}_RemoveComparison_click`,
  }
}

/**
 * @description mega menu 商品卡片曝光/点击事件名
 * @reason 商城悬停用 ProductCard；家庭/企业/安防悬停用 Card（对齐需求）
 * @param {string} prefix
 * @returns {{ exposure: string, click: string }}
 */
export function buildMegaMenuProductCardEvents(prefix) {
  const cardKey = prefix === 'MallHomepage' ? 'ProductCard' : 'Card'
  return {
    exposure: `pv_${prefix}_Hover_${cardKey}`,
    click: `pv_${prefix}_Hover_${cardKey}_click`,
  }
}

/**
 * @description 资料/文件点击埋点 payload
 * @param {Object} options
 * @returns {Object}
 */
export function buildContentFileClickPayload({ pvEvent, pageName, fileName, actionType, extra = {} }) {
  return {
    pvEvent,
    targetType: 'content_file',
    targetId: fileName,
    targetName: fileName,
    pageName,
    properties: {
      file_name: fileName,
      action_type: actionType,
      ...extra,
    },
  }
}
